import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Calendar, Clock, Sparkles, ArrowRight, Dna, HeartPulse, X, CheckCircle2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card'
import { Button } from './ui/Button'

interface TimelineItem {
  id: string
  quarter: string
  year: number
  title: string
  description: string
  features: string[]
  status: 'planned' | 'development' | 'testing' | 'launching'
  icon: React.ReactNode
  detailedVision?: string
}

const upcomingFeatures: TimelineItem[] = [
  {
    id: '1',
    quarter: 'Q4',
    year: 2026,
    title: 'Advanced AI Health Coach',
    description: 'Personalized health coaching powered by advanced AI algorithms and your biomarker data.',
    features: [
      '24/7 AI Health Assistant',
      'Personalized Protocol Recommendations',
      'Real-time Health Insights',
      'Smart Goal Tracking'
    ],
    status: 'development',
    icon: <Sparkles className="w-6 h-6" />,
    detailedVision: 'Our AI Health Coach isn\'t just a chatbot; it\'s a synthesized medical mind that lives in your pocket. By combining LLM capabilities with your real-time biomarker stream, it provides sub-second advice on whether you should push harder in a workout or skip it based on your systemic inflammation levels.'
  },
  {
    id: '2',
    quarter: 'Q1',
    year: 2027,
    title: 'Genetic Integration Platform',
    description: 'Seamless integration of genetic data with biomarker analysis for comprehensive health insights.',
    features: [
      'DNA-based Health Recommendations',
      'Genetic Risk Assessment',
      'Personalized Supplement Protocols',
      'Inherited Condition Screening'
    ],
    status: 'planned',
    icon: <Dna className="w-6 h-6" />,
    detailedVision: 'The Genetic Integration Platform will cross-reference your SNP data with monthly blood results. For example, if you have a MTHFR variant, the platform will automatically adjust your folate supplement protocol in real-time as your serum levels fluctuate.'
  },
  {
    id: '3',
    quarter: 'Q2',
    year: 2027,
    title: 'Wearable Device Ecosystem',
    description: 'Complete integration with popular wearable devices for continuous health monitoring.',
    features: [
      'Real-time Biometric Monitoring',
      'Sleep Quality Analysis',
      'Activity and Recovery Tracking',
      'Stress Level Management'
    ],
    status: 'planned',
    icon: <HeartPulse className="w-6 h-6" />,
    detailedVision: 'We are building a unified GraphQL gateway that ingests data from Oura, Whoop, and Apple Watch to create a single "Stress vs. Capacity" score. This prevents overtraining and ensures your autonomic nervous system remains in a state of high-vibrational coherence.'
  }
]

const FeatureModal: React.FC<{ item: TimelineItem; onClose: () => void }> = ({ item, onClose }) => (
  <motion.div 
    initial={{ opacity: 0 }} 
    animate={{ opacity: 1 }} 
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-xl"
    onClick={onClose}
  >
    <motion.div 
      initial={{ scale: 0.9, y: 20 }}
      animate={{ scale: 1, y: 0 }}
      exit={{ scale: 0.9, y: 20 }}
      className="bg-theme-surface border border-theme-border rounded-[32px] max-w-2xl w-full p-8 relative overflow-hidden"
      onClick={e => e.stopPropagation()}
    >
      <div className="absolute top-0 right-0 p-8 opacity-5 -z-10">
        {item.icon}
      </div>
      
      <button 
        onClick={onClose}
        className="absolute top-6 right-6 p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
      >
        <X className="w-5 h-5 text-theme-text-primary" />
      </button>

      <div className="flex items-center gap-4 mb-8">
        <div className="w-16 h-16 rounded-2xl bg-theme-primary/10 flex items-center justify-center">
          <div className="text-theme-primary">
            {item.icon}
          </div>
        </div>
        <div>
          <div className="text-sm font-black text-theme-primary uppercase tracking-widest mb-1">{item.quarter} {item.year}</div>
          <h3 className="text-3xl font-black text-theme-text-primary">{item.title}</h3>
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <h4 className="text-xs font-bold text-theme-text-muted uppercase tracking-widest mb-3">The Vision</h4>
          <p className="text-xl text-theme-text-primary font-light leading-relaxed">
            {item.detailedVision}
          </p>
        </div>

        <div>
          <h4 className="text-xs font-bold text-theme-text-muted uppercase tracking-widest mb-3">Key Capabilities</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {item.features.map(f => (
              <div key={f} className="flex items-center gap-3 p-3 rounded-xl bg-theme-primary/5 border border-theme-primary/10">
                <CheckCircle2 className="w-4 h-4 text-theme-primary" />
                <span className="text-sm font-medium text-theme-text-secondary">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-10 pt-8 border-t border-theme-border flex justify-end">
        <Button onClick={onClose} className="rounded-full px-8 py-6 text-lg font-bold">
          Notify Me at Launch
        </Button>
      </div>
    </motion.div>
  </motion.div>
)

export const UpcomingFeaturesTimeline: React.FC = () => {
  const [selectedItem, setSelectedItem] = useState<TimelineItem | null>(null);

  return (
    <section className="py-20 bg-theme-surface light-surface relative overflow-hidden" aria-labelledby="upcoming-features-title">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-r from-theme-primary/5 via-transparent to-theme-secondary/5" />
      
      <div className="container mx-auto px-4 relative">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <motion.h2 
            id="upcoming-features-title"
            className="text-4xl md:text-5xl font-display font-bold gradient-heading mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            Upcoming Features
          </motion.h2>
          <motion.p 
            className="text-xl subtitle-muted max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Exciting new features coming to BioTune to enhance your health optimization journey.
          </motion.p>
        </motion.div>

        {/* Timeline */}
        <div className="relative" role="list" aria-label="Upcoming features timeline">
          {/* Timeline line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-theme-primary via-theme-secondary to-theme-primary opacity-20" />
          
          {upcomingFeatures.map((item, index) => (
            <motion.div
              key={item.id}
              className={`relative mb-16 flex justify-center ${index % 2 === 0 ? 'lg:justify-end' : 'lg:justify-start'}`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              {/* Timeline dot */}
              <div className="absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 top-1/2 z-10 hidden lg:block">
                <div className="w-6 h-6 glass-surface rounded-full flex items-center justify-center shadow-lg border-2 border-theme-primary">
                  <div className="w-3 h-3 bg-gradient-to-r from-theme-primary to-theme-secondary rounded-full" />
                </div>
              </div>
              
              {/* Content Card */}
              <div className={`w-full lg:w-[45%] ${index % 2 === 0 ? 'lg:pr-12' : 'lg:pl-12'}`}>
                <motion.div 
                  whileHover={{ y: -6, scale: 1.02 }} 
                  className="group cursor-pointer"
                  onClick={() => setSelectedItem(item)}
                >
                  <Card className="border border-theme-border bg-theme-surface light-surface hover:shadow-2xl transition-all duration-300 rounded-3xl p-2">
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 bg-theme-primary/10 rounded-full flex items-center justify-center shadow-lg">
                            <Calendar className="w-5 h-5 text-theme-primary" />
                          </div>
                          <div>
                            <div className="text-sm font-black text-theme-primary">
                              {item.quarter} {item.year}
                            </div>
                          </div>
                        </div>
                        <div className="text-[10px] font-black uppercase tracking-widest px-2 py-1 bg-white/5 border border-white/10 rounded-lg opacity-50">
                          {item.status}
                        </div>
                      </div>
                      
                      <CardTitle className="text-2xl font-black text-theme-text-primary flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-theme-primary/5 flex items-center justify-center">
                          {item.icon}
                        </div>
                        {item.title}
                      </CardTitle>
                      <p className="text-theme-text-secondary font-medium leading-relaxed">
                        {item.description}
                      </p>
                    </CardHeader>
                    
                    <CardContent>
                      <div className="flex items-center justify-between pt-4 border-t border-theme-border">
                        <span className="text-xs font-bold text-theme-text-muted uppercase tracking-widest flex items-center gap-2">
                          <Clock className="w-3 h-3" /> Coming Soon
                        </span>
                        <div className="flex items-center gap-1 text-theme-primary font-bold text-sm group-hover:gap-2 transition-all">
                          Learn More <ArrowRight className="w-4 h-4" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
        
        {/* CTA */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <button 
            className="bg-gradient-to-r from-theme-primary to-theme-secondary px-8 py-4 rounded-full font-black text-theme-text-inverse shadow-xl hover:shadow-theme-primary/25 hover:scale-105 active:scale-95 transition-all flex items-center gap-3 mx-auto uppercase tracking-tighter" 
            onClick={() => setSelectedItem(upcomingFeatures[0])}
          >
            <Sparkles className="w-5 h-5" />
            Join Alpha Testing
          </button>
        </motion.div>
      </div>

      <AnimatePresence>
        {selectedItem && (
          <FeatureModal item={selectedItem} onClose={() => setSelectedItem(null)} />
        )}
      </AnimatePresence>
    </section>
  )
}

export default UpcomingFeaturesTimeline
